'use client';

import { faHeart as faHeartOutline } from '@fortawesome/pro-regular-svg-icons';
import { faCartShopping } from '@fortawesome/pro-solid-svg-icons';
import { useRouter } from 'next/navigation';
import { FC } from 'react';
import FeatureFlag from '@/components/common/feature-flag/feature-flag';
import FormattedPrice from '@/components/common/formatted-price/formatted-price';
import { Button, Card, CardBody, CardFooter, Icon, IconButton, Image } from '@/components/ui';
import useCoreConfig from '@/hooks/use-core-config';
import { useTranslation } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';
import { MyCartActionButtonsEnum } from '@/types/action-buttons/action-buttons';
import { SimpleProductInfo } from '@/types/products/simple-product-info';
import { cn } from '@/utils';
import type { ContentVariant } from '@/utils/content-variant-from-background';
import { selectMainImage } from '@/utils/select-main-image';
import { buildStudioProductDetailPath } from '@/utils/studio-preview-locale-url';

interface ProductCardStudioPreviewProps {
  product: SimpleProductInfo;
  noMaxWidth?: boolean;
  interactive?: boolean;
  showAddToCart?: boolean;
  showFavorites?: boolean;
  contentVariant?: ContentVariant;
  addToCartButtonType?: MyCartActionButtonsEnum;
}

enum ProductCardVersions {
  VERSION_1 = 'VERSION_1',
  VERSION_2 = 'VERSION_2',
  VERSION_3 = 'VERSION_3',
}

/** Static product card for studio preview — no cart, favorites, or pricing API hooks. */
const ProductCardStudioPreview: FC<ProductCardStudioPreviewProps> = ({
  product,
  noMaxWidth,
  interactive = true,
  showAddToCart = false,
  showFavorites = false,
  contentVariant = 'light',
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { keycloak } = useKeycloak();
  const isAuthenticated = keycloak?.authenticated;
  const {
    variants: { productCard: productCardVersion },
    features,
    productCardStyle,
  } = useCoreConfig();

  const isDarkSurface = contentVariant === 'dark';
  const primaryPriceColor = isDarkSurface
    ? 'white'
    : productCardVersion === ProductCardVersions.VERSION_2
      ? 'black'
      : 'gray';
  const mutedPriceColor = isDarkSurface ? 'whiteMuted' : 'gray';
  const hasDiscount = product.discountedPrice > 0 && product.discountedPrice < product.price;
  const mainImageSrc = selectMainImage(product.images, { width: 516, quality: 75 });
  const shouldAllowCart = (!isAuthenticated && features.cartForGuests) || isAuthenticated;
  const showCartButton = (interactive || showAddToCart) && (shouldAllowCart || showAddToCart);
  const showFavoritesButton = interactive || showFavorites;
  const isVersion2ProductCard = productCardVersion === ProductCardVersions.VERSION_2;
  const productCardFontWeight = isVersion2ProductCard ? ('bold' as const) : undefined;
  const productMetaUrl = product.meta?.url?.trim();

  return (
    <div
      className={cn('h-full', productMetaUrl && 'cursor-pointer')}
      onClick={() => {
        if (productMetaUrl) {
          router.push(buildStudioProductDetailPath(productMetaUrl));
        }
      }}
    >
      <Card
        className={cn(
          'relative h-full min-h-[340px] overflow-hidden rounded-2xl bg-transparent p-0 shadow-none @lg:min-h-0 @lg:p-2',
          {
            'bg-default-50': productCardVersion === ProductCardVersions.VERSION_1,
            'max-w-[258px]': !noMaxWidth,
          },
        )}
        data-testid="product-card__card"
      >
        {showFavoritesButton ? (
          <FeatureFlag feature="favorites">
            <div className="absolute top-3 right-3 z-30">
              <IconButton
                aria-label="Toggle favorite"
                data-testid="product-card__wishlist-button"
                className="bg-white"
                variant="flat"
                isDisabled
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <Icon icon={faHeartOutline} className="!text-base" />
              </IconButton>
            </div>
          </FeatureFlag>
        ) : null}

        <CardBody className="overflow-visible p-0">
          <div className="relative aspect-square w-full overflow-hidden rounded-2xl">
            <Image
              src={mainImageSrc}
              alt={product.name}
              className="h-full w-full object-cover"
              radius="none"
            />
          </div>
          <div className="flex flex-col gap-1 px-2 py-3">
            <p
              className={cn('line-clamp-2 text-sm font-medium', {
                'font-bold': isVersion2ProductCard,
              })}
              style={
                productCardStyle?.titleColor ? { color: productCardStyle.titleColor } : undefined
              }
            >
              {product.name}
            </p>
            <div className="flex items-center gap-2">
              <FormattedPrice
                price={hasDiscount ? product.discountedPrice : product.price}
                color={productCardStyle?.priceColor ? undefined : primaryPriceColor}
                customColor={hasDiscount ? undefined : productCardStyle?.priceColor}
                fontWeight={productCardFontWeight}
                showVatSuffix={features.priceForGuests !== false}
                vatSuffixColor={productCardStyle?.vatBadgeColor}
                vatSuffixBackground={productCardStyle?.vatBadgeBackground}
              />
              {hasDiscount ? (
                <FormattedPrice
                  price={product.price}
                  color={mutedPriceColor}
                  fontWeight={productCardFontWeight}
                  lineThrough
                  showVatSuffix={false}
                />
              ) : null}
            </div>
          </div>
        </CardBody>

        {showCartButton ? (
          <CardFooter className="px-2 pt-0 pb-3">
            <Button
              color="primary"
              className="w-full min-w-0"
              isDisabled
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {t?.common.addToCart ?? 'Add to cart'}
              <Icon icon={faCartShopping} className="ml-2" />
            </Button>
          </CardFooter>
        ) : null}
      </Card>
    </div>
  );
};

export default ProductCardStudioPreview;
